/* eslint-disable max-len */
/* eslint-disable no-useless-catch, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { BasicUtils } from 'src/app/shared/utils/basic.utils';

import { MaterialModel } from '../../modules/materials/models/material.model';
import {
  EOfflineStorageVideoType,
  OfflineStorageMetadata,
  ShakaPlayerInstance,
  ShakaStorageInstance,
  ShakaStore
} from '../types/shaka-player.types';
import { VideoFormat } from '../types/video';

// Códigos de erro do Shaka Player
export enum ShakaErrorCategory {
  NETWORK = 1,
  TEXT = 2,
  MEDIA = 3,
  MANIFEST = 4,
  STREAMING = 5,
  DRM = 6,
  PLAYER = 7,
  CAST = 8,
  STORAGE = 9,
  ADS = 10
}

export enum ShakaErrorCode {
  // Storage errors
  STORAGE_DISABLED = 9001,
  STORAGE_NOT_SUPPORTED = 9002,
  STORAGE_ALREADY_IN_PROGRESS = 9003,
  STORAGE_INVALID_CONTENT = 9004,
  STORAGE_UNSUPPORTED_OPERATION = 9005,
  STORAGE_HTTP_ERROR = 9006,
  STORAGE_INVALID_MANIFEST = 9007,
  STORAGE_MALFORMED_OFFLINE_URI = 9008,
  STORAGE_CANNOT_INSERT_DUPLICATE = 9009,
  STORAGE_OPERATION_ABORTED = 9010,
  STORAGE_KEY_NOT_FOUND = 9012,
  STORAGE_CORRUPTED_DATA = 9013,

  // Network errors
  NETWORK_TIMEOUT = 1001,
  NETWORK_HTTP_ERROR = 1002,

  // Player errors
  PLAYER_LOAD_INTERRUPTED = 7000,
  PLAYER_DESTROYED = 7001
}

export enum ShakaErrorSeverity {
  RECOVERABLE = 1,
  CRITICAL = 2
}

export interface ShakaError {
  category: ShakaErrorCategory;
  code: ShakaErrorCode;
  data?: unknown[];
  handled?: boolean;
  message: string;
  severity: ShakaErrorSeverity;
  stack?: string;
}

export interface ShakaAbortableOperation {
  promise: Promise<unknown>;
  abort: () => Promise<void>;
}

export type ProgressDownload = {
  videoId: string;
  completed: boolean;
  progress: number;
};

type StoreDownload = {
  link: string;
  metadata: OfflineStorageMetadata;
};

type QueuedStoreDownload = StoreDownload & {
  resolve?: (value: ShakaStore) => void;
  reject?: (error: unknown) => void;
};

export interface OfflineShakaConfig {
  maxConcurrentDownloads?: number;
}

export class OfflineShaka {
  private readonly MAX_CONCURRENT_DOWNLOADS_LIMIT = 5; // Limite máximo interno

  private player: ShakaPlayerInstance | null = null;
  private storage: ShakaStorageInstance | null = null;
  private downloadQueue: QueuedStoreDownload[] = [];
  private isProcessingQueue = false;
  private activeDownloads = new Map<string, StoreDownload>();
  private progressSubjects = new Map<
    string,
    BehaviorSubject<ProgressDownload>
  >();
  private currentProcessingVideos = new Set<string>();
  private storageInstances = new Map<string, ShakaStorageInstance>();
  private abortableOperations = new Map<string, ShakaAbortableOperation>();
  private queueProcessor: NodeJS.Timeout | null = null;

  private maxConcurrentDownloads = 3;

  constructor(
    player: ShakaPlayerInstance | null = null,
    config: OfflineShakaConfig = {}
  ) {
    const { maxConcurrentDownloads = 3 } = config;
    this.setMaxConcurrentDownloads(maxConcurrentDownloads);
    this.player = player;
    this.initStorage(player);
  }

  private initStorage(player: ShakaPlayerInstance | null): void {
    if (!shaka) {
      throw new Error('Shaka Player does not exist on the window object.');
    }

    // Se há player, inicializar storage normalmente
    if (player) {
      this.storage = new shaka.offline.Storage(player);
      return;
    }

    // Se não há player, storage será null - será criado dinamicamente quando necessário
    this.storage = null;
  }

  // Método auxiliar para garantir que temos storage para operações read-only
  private async ensureStorageForReadOnly(): Promise<ShakaStorageInstance> {
    if (this.storage) {
      return this.storage;
    }

    // Para operações read-only, criar um player temporário apenas para inicializar o storage
    // Isso é necessário porque o Shaka Player exige um player para acessar o storage
    const tempPlayer = new shaka.Player();
    return new shaka.offline.Storage(tempPlayer);
  }

  // Método auxiliar para garantir que temos storage principal para operações de escrita
  private async ensureStorageForWriteOperations(): Promise<ShakaStorageInstance> {
    if (this.storage) {
      return this.storage;
    }

    throw new Error(
      'Player é necessário para operações de download/remoção. Inicialize a classe com um player válido.'
    );
  }

  private createStorageForDownload(videoId: string): ShakaStorageInstance {
    const storage = new shaka.offline.Storage(this.player);
    storage.configure({
      offline: {
        progressCallback: (_: unknown, progress: number) => {
          progress = Math.round(progress * 100);
          this.setDownloadProgress(progress, videoId);
        }
      }
    });
    return storage;
  }

  private setDownloadProgress(progress: number, videoId: string): void {
    const isCompleted = progress === 100;

    if (!this.progressSubjects.has(videoId)) {
      this.progressSubjects.set(
        videoId,
        new BehaviorSubject<ProgressDownload>({
          videoId,
          progress: 0,
          completed: false
        })
      );
    }

    const progressSubject = this.progressSubjects.get(videoId)!;
    progressSubject.next({
      progress,
      completed: isCompleted,
      videoId
    });

    if (isCompleted) {
      timer(500)
        .pipe(take(1))
        .subscribe(() => {
          progressSubject.complete();
          this.progressSubjects.delete(videoId);
        });
    }
  }

  // Método para configurar o número máximo de downloads concorrentes
  setMaxConcurrentDownloads(max: number): void {
    if (max < 1 || max > this.MAX_CONCURRENT_DOWNLOADS_LIMIT) {
      throw new Error(
        `maxConcurrentDownloads deve ser entre 1 e ${this.MAX_CONCURRENT_DOWNLOADS_LIMIT}`
      );
    }
    this.maxConcurrentDownloads = Math.max(
      1,
      Math.min(max, this.MAX_CONCURRENT_DOWNLOADS_LIMIT)
    );
  }

  // Método público para obter o observable de progresso de um vídeo específico
  getProgressObservable(videoId: string): Observable<ProgressDownload> {
    if (!this.progressSubjects.has(videoId)) {
      this.progressSubjects.set(
        videoId,
        new BehaviorSubject<ProgressDownload>({
          videoId,
          progress: 0,
          completed: false
        })
      );
    }

    return this.progressSubjects
      .get(videoId)!
      .asObservable()
      .pipe(
        distinctUntilChanged((prev, curr) => prev.progress === curr.progress)
      );
  }

  async download(store: StoreDownload): Promise<ShakaStore> {
    // Verificar se temos player e storage adequados para download
    if (!this.player || !this.storage) {
      throw new Error(
        'Player é necessário para operações de download. Inicialize a classe com um player válido.'
      );
    }

    const videoId = store.metadata.id;
    this.activeDownloads.set(videoId, store);

    return new Promise((resolve, reject) => {
      const storeWithCallbacks = {
        ...store,
        resolve,
        reject
      };

      this.downloadQueue.push(storeWithCallbacks);
      this.setDownloadProgress(0, videoId);

      void this.processDownloadQueue();
    });
  }

  private async processDownloadQueue(): Promise<void> {
    // Se já estiver processando, agenda para processar depois
    if (this.isProcessingQueue) {
      if (!this.queueProcessor) {
        this.queueProcessor = setTimeout(() => {
          this.queueProcessor = null;
          void this.processDownloadQueue();
        }, 50);
      }
      return;
    }

    if (this.downloadQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    try {
      // Processar apenas uma vez por chamada, não em loop
      const slotsAvailable =
        this.maxConcurrentDownloads - this.currentProcessingVideos.size;

      if (slotsAvailable > 0) {
        // Iniciar downloads nos slots disponíveis
        const downloadsToStart = Math.min(
          slotsAvailable,
          this.downloadQueue.length
        );

        for (let i = 0; i < downloadsToStart; i++) {
          const store = this.downloadQueue.shift()!;
          // Iniciar download sem aguardar (fire and forget)
          this.performSingleDownload(store).catch((error) => {
            console.error(
              `Erro no download do vídeo ${store.metadata.id}:`,
              error
            );
            store.reject?.(error);
            this.activeDownloads.delete(store.metadata.id);
          });
        }
      }

      // Se ainda há itens na fila, agenda próximo processamento
      if (this.downloadQueue.length > 0) {
        this.queueProcessor = setTimeout(() => {
          this.queueProcessor = null;
          void this.processDownloadQueue();
        }, 100); // Verifica novamente em 100ms
      }
    } finally {
      this.isProcessingQueue = false;
    }
  }

  private async performSingleDownload(
    store: QueuedStoreDownload
  ): Promise<void> {
    const videoId = store.metadata.id;

    // Adicionar o vídeo ao conjunto de downloads ativos
    this.currentProcessingVideos.add(videoId);
    this.setDownloadProgress(0, videoId);

    // Criar uma instância de storage específica para este download
    const downloadStorage = this.createStorageForDownload(videoId);
    this.storageInstances.set(videoId, downloadStorage);

    try {
      const abortableOperation = downloadStorage.store(
        encodeURI(store.link),
        store.metadata
      );

      // Armazenar a operação para poder cancelá-la se necessário
      this.abortableOperations.set(videoId, abortableOperation);

      const result = await abortableOperation.promise;

      // Remover da lista de operações ativas após conclusão
      this.abortableOperations.delete(videoId);

      // Resolver a Promise com o resultado
      store.resolve?.(result);
    } catch (error) {
      console.error('Erro durante o download:', error);

      // Limpar operação em caso de erro
      this.abortableOperations.delete(videoId);

      this.setDownloadProgress(0, videoId);
      this.activeDownloads.delete(videoId);

      // Se o erro for relacionado ao storage corrompido, tentar limpar
      const shakaError = error as ShakaError;
      if (shakaError?.code === ShakaErrorCode.STORAGE_KEY_NOT_FOUND) {
        console.warn('Storage corrompido detectado durante download');
        await this.cleanCorruptedStorage();
      }

      // Rejeitar a Promise com o erro
      store.reject?.(error);
    } finally {
      // Remover o vídeo do conjunto de downloads ativos
      this.currentProcessingVideos.delete(videoId);
      // Limpar a instância de storage específica
      this.storageInstances.delete(videoId);
      // Limpar operação abortável
      this.abortableOperations.delete(videoId);

      // Se ainda há downloads na fila, tentar processá-los
      if (this.downloadQueue.length > 0) {
        // Usar setImmediate para processar na próxima iteração do event loop
        setImmediate(() => {
          void this.processDownloadQueue();
        });
      }
    }
  }

  // Método para cancelar um download específico
  async cancelSingleDownload(videoId: string): Promise<void> {
    try {
      // Cancelar operação abortável se estiver ativa
      const abortableOperation = this.abortableOperations.get(videoId);
      if (abortableOperation) {
        await abortableOperation.abort().catch(() => {
          // Ignora erros de abort
        });
        this.abortableOperations.delete(videoId);
      }

      // Cancelar storage específico se estiver ativo
      const storage = this.storageInstances.get(videoId);
      if (storage) {
        await storage.cancel().catch(() => {
          // Ignora erros de cancelamento
        });
        this.storageInstances.delete(videoId);
      }

      // Remover da fila se estiver aguardando
      const queueIndex = this.downloadQueue.findIndex(
        (store) => store.metadata.id === videoId
      );
      if (queueIndex >= 0) {
        const store = this.downloadQueue[queueIndex];
        this.downloadQueue.splice(queueIndex, 1);
        store.reject?.(new Error('Download cancelado pelo usuário'));
      }

      // Limpar estado
      this.currentProcessingVideos.delete(videoId);
      this.activeDownloads.delete(videoId);

      // Resetar progresso
      this.setDownloadProgress(0, videoId);
    } catch (error) {
      console.error(`Erro ao cancelar download do vídeo ${videoId}:`, error);
    }
  }

  // Verificar se um vídeo está na fila (aguardando ou sendo baixado)
  isVideoInQueue(videoId: string): boolean {
    const inProcessing = this.currentProcessingVideos.has(videoId);
    const inQueue = this.downloadQueue.some(
      (store) => store.metadata.id === videoId
    );

    return inProcessing || inQueue;
  }

  // Verificar se um vídeo está sendo baixado ativamente
  isVideoDownloading(videoId: string): boolean {
    const isDownloading = this.currentProcessingVideos.has(videoId);
    const hasAbortableOperation = this.abortableOperations.has(videoId);

    return isDownloading && hasAbortableOperation;
  }

  async cancelDownload(): Promise<void> {
    try {
      // Limpar timer de processamento da fila
      if (this.queueProcessor) {
        clearTimeout(this.queueProcessor);
        this.queueProcessor = null;
      }

      // Cancelar todas as operações abortáveis ativas
      const abortPromises = Array.from(this.abortableOperations.values()).map(
        (operation) =>
          operation.abort().catch(() => {
            // Ignora erros de abort
          })
      );
      await Promise.allSettled(abortPromises);

      // Cancelar todas as instâncias de storage ativas
      const cancelPromises = Array.from(this.storageInstances.values()).map(
        (storage) =>
          storage.cancel().catch(() => {
            // Ignora erros de cancelamento
          })
      );
      await Promise.allSettled(cancelPromises);

      // Limpar fila de downloads
      this.downloadQueue.forEach((store) => {
        store.reject?.(new Error('Download cancelado'));
      });
      this.downloadQueue = [];
      this.isProcessingQueue = false;

      // Limpar todas as instâncias de storage e operações
      this.storageInstances.clear();
      this.abortableOperations.clear();
      this.currentProcessingVideos.clear();

      // Limpar todos os subjects de progresso
      this.progressSubjects.forEach((subject, videoId) => {
        subject.next({
          videoId,
          progress: 0,
          completed: false
        });
        subject.complete();
      });
      this.progressSubjects.clear();

      // Cancelar storage principal se estiver disponível
      if (this.storage) {
        await this.storage.cancel();
      }

      // Limpar downloads ativos
      this.activeDownloads.clear();
    } catch (error) {
      console.error('Erro ao cancelar download:', error);
    }
  }

  async removeOfflineVideo(videoId: string): Promise<boolean> {
    try {
      const storage = await this.ensureStorageForWriteOperations();
      const storageList = (await storage.list()) as ShakaStore[];
      const videoToRemove = storageList.find(
        (storage) => storage.appMetadata.id === videoId
      );

      if (videoToRemove) {
        await storage.remove(videoToRemove.offlineUri);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao remover vídeo offline:', error);
      return false;
    }
  }

  async clearAllOfflineStorage(): Promise<void> {
    try {
      const storage = await this.ensureStorageForWriteOperations();
      const storageList = (await storage.list()) as ShakaStore[];
      for (const item of storageList) {
        try {
          await storage.remove(item.offlineUri);
        } catch (error) {
          console.error('Erro ao remover item:', item, error);
        }
      }
    } catch (error) {
      console.error('Erro ao limpar todo o storage:', error);
    }
  }

  async checkStorageIntegrity(): Promise<{
    isHealthy: boolean;
    corruptedItems: number;
    totalItems: number;
  }> {
    try {
      const storage = await this.ensureStorageForReadOnly();
      const storageList = (await storage.list()) as ShakaStore[];
      let corruptedItems = 0;

      for (const item of storageList) {
        try {
          // Verificar se o item tem as propriedades essenciais
          if (
            !item.offlineUri ||
            !item.appMetadata ||
            !item.appMetadata.id ||
            !item.appMetadata.title
          ) {
            corruptedItems++;
          }
        } catch {
          corruptedItems++;
        }
      }

      return {
        isHealthy: corruptedItems === 0,
        corruptedItems,
        totalItems: storageList.length
      };
    } catch (error) {
      console.error('Erro ao verificar integridade do storage:', error);
      return {
        isHealthy: false,
        corruptedItems: -1,
        totalItems: -1
      };
    }
  }

  async getOfflineVideos(
    ids: string[],
    type: EOfflineStorageVideoType
  ): Promise<ShakaStore[]> {
    try {
      const storage = await this.ensureStorageForReadOnly();
      const storageList = (await storage.list()) as ShakaStore[];
      return (
        storageList?.filter(
          (storage: ShakaStore) =>
            ids.includes(storage.appMetadata.id) &&
            storage.appMetadata.type === type
        ) ?? []
      );
    } catch (error) {
      console.error('Erro ao buscar vídeos offline:', error);
      // Se houver erro ao listar, tentar limpar storage corrompido
      await this.cleanCorruptedStorage();
      return [];
    }
  }

  private async cleanCorruptedStorage(): Promise<void> {
    try {
      console.warn('Tentando limpar storage corrompido...');
      // Para limpeza, precisamos de storage com capacidades de escrita
      const storage = this.storage || (await this.ensureStorageForReadOnly());
      const storageList = (await storage.list()) as ShakaStore[];

      // Verificar e remover entradas corrompidas
      for (const item of storageList || []) {
        try {
          // Tentar acessar o item para verificar se está corrompido
          if (!item.offlineUri || !item.appMetadata) {
            console.warn('Removendo item corrompido:', item);
            await storage.remove(item.offlineUri || item.originalManifestUri);
          }
        } catch (itemError) {
          console.warn('Erro ao verificar item, removendo:', item, itemError);
          try {
            await storage.remove(item.offlineUri || item.originalManifestUri);
          } catch (removeError) {
            console.error('Erro ao remover item corrompido:', removeError);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao limpar storage corrompido:', error);
    }
  }

  async getOfflineSerieVideos(serieId: string): Promise<VideoFormat[]> {
    const storage = await this.ensureStorageForReadOnly();
    const storageList = (await storage.list()) as ShakaStore[];
    return (
      storageList
        ?.filter(
          (storage: ShakaStore) =>
            storage.appMetadata.serie?.id === serieId &&
            storage.appMetadata.type === EOfflineStorageVideoType.serie
        )
        .map(
          (s) =>
            <VideoFormat>{
              id: s.appMetadata.id,
              titulo: s.appMetadata.title,
              capa: {
                formats: {
                  thumbnail: {
                    name: s.appMetadata.title,
                    url: s.appMetadata.thumbnail
                  }
                }
              },
              link: s.offlineUri
            }
        ) ?? []
    );
  }

  async getAllOfflineSeries(): Promise<MaterialModel[]> {
    const storage = await this.ensureStorageForReadOnly();
    const storageList = (await storage.list()) as ShakaStore[];
    const seriesShaka = (
      storageList?.filter(
        (storage: ShakaStore) =>
          storage.appMetadata.type === EOfflineStorageVideoType.serie
      ) ?? []
    ).map(
      (s) =>
        <MaterialModel>{
          id: s.appMetadata.serie.id,
          path: '/offline/seriado/' + s.appMetadata.serie.id,
          titulo: s.appMetadata.serie.title,
          capa: {
            formats: {
              thumbnail: {
                name: s.appMetadata.serie.title,
                url: s.appMetadata.serie.thumbnail
              }
            }
          }
        }
    );

    const series = BasicUtils.groupBy(seriesShaka, (item: MaterialModel) => {
      return [item.id, item.titulo] as [string, string];
    }).map(
      (s: MaterialModel[]) =>
        <MaterialModel>{
          id: s[0].id,
          path: s[0].path,
          titulo: s[0].titulo,
          capa: s[0].capa
        }
    );

    return series;
  }

  async getOfflineSerieById(id: string): Promise<MaterialModel> {
    const storage = await this.ensureStorageForReadOnly();
    const storageList = (await storage.list()) as ShakaStore[];
    const seriesShaka = (
      storageList?.filter(
        (storage: ShakaStore) =>
          storage.appMetadata.type === EOfflineStorageVideoType.serie &&
          storage.appMetadata.serie.id === id
      ) ?? []
    ).map(
      (s) =>
        <MaterialModel>{
          id: s.appMetadata.serie.id,
          path: '/offline/seriado/' + s.appMetadata.serie.id,
          titulo: s.appMetadata.serie.title,
          capa: {
            formats: {
              thumbnail: {
                name: s.appMetadata.serie.title,
                url: s.appMetadata.serie.thumbnail
              }
            }
          }
        }
    );

    const series = BasicUtils.groupBy(seriesShaka, (item: MaterialModel) => {
      return [item.id, item.titulo] as [string, string];
    }).map(
      (s: MaterialModel[]) =>
        <MaterialModel>{
          id: s[0].id,
          path: s[0].path,
          titulo: s[0].titulo,
          capa: s[0].capa
        }
    );

    return series[0];
  }

  async getAllOfflineVideos(): Promise<MaterialModel[]> {
    const storage = await this.ensureStorageForReadOnly();
    const storageList: ShakaStore[] = await storage.list();
    return (
      storageList
        ?.filter(
          (storage: ShakaStore) =>
            storage.appMetadata.type === EOfflineStorageVideoType.video
        )
        .map(
          (s) =>
            <MaterialModel>{
              id: s.appMetadata.id,
              path: '/video/' + s.appMetadata.id,
              titulo: s.appMetadata.title,
              capa: {
                formats: {
                  thumbnail: {
                    name: s.appMetadata.title,
                    url: s.appMetadata.thumbnail
                  }
                }
              }
            }
        ) ?? []
    );
  }

  // Métodos para monitorar a fila de downloads
  getQueueLength(): number {
    return this.downloadQueue.length + this.currentProcessingVideos.size;
  }

  getQueuedCount(): number {
    return this.downloadQueue.length;
  }

  getActiveDownloadsCount(): number {
    return this.currentProcessingVideos.size;
  }

  isDownloadInProgress(): boolean {
    return this.isProcessingQueue || this.currentProcessingVideos.size > 0;
  }

  getCurrentDownloadId(): string | null {
    return this.downloadQueue[0]?.metadata?.id ?? null;
  }
}
